import { isPicUserImageUrl, picUserImageUrl } from '@/common/utils/picUserImageUrl';

const NO_DOCTOR_PROFILE_IMAGE_MEN = 'https://www.paziresh24.com/api/getImage/p24/search-men/noimage.png';
const NO_DOCTOR_PROFILE_IMAGE_WOMEN = 'https://www.paziresh24.com/api/getImage/p24/search-women/noimage.png';
const NO_DOCTOR_PROFILE_IMAGE = NO_DOCTOR_PROFILE_IMAGE_MEN;

type DoctorImageSource = {
  user_id?: string | null;
  image?: string | null;
  gender?: unknown;
};

function isFemaleDoctorGender(gender: unknown): boolean {
  if (gender == null || gender === '') return false;
  if (gender === false) return true;
  const asNumber = Number(gender);
  if (asNumber === 2) return true;
  if (typeof gender === 'string') {
    const normalized = gender.trim().toLowerCase();
    if (normalized === 'female' || normalized === 'f' || normalized === 'woman' || normalized === 'women') {
      return true;
    }
  }
  return false;
}

export function defaultDoctorProfileImageUrl(gender?: unknown): string {
  return isFemaleDoctorGender(gender) ? NO_DOCTOR_PROFILE_IMAGE_WOMEN : NO_DOCTOR_PROFILE_IMAGE_MEN;
}

export function isDefaultDoctorProfileImage(src?: string | null): boolean {
  if (!src?.trim()) return false;
  return /search-(?:men|women)\/noimage\.png/i.test(src);
}

export function displayedDoctorProfileImageUrl(
  src: string | undefined | null,
  gender?: unknown,
  loadFailed = false,
): string {
  if (loadFailed) return defaultDoctorProfileImageUrl(gender);
  const trimmed = typeof src === 'string' ? src.trim() : '';
  if (!trimmed || isDefaultDoctorProfileImage(trimmed) || /noimage/i.test(trimmed)) {
    return defaultDoctorProfileImageUrl(gender);
  }
  return trimmed;
}

/** Full-size URL for Open Graph / JSON-LD. */
export function resolveDoctorProfileImageUrl(information: DoctorImageSource | undefined, pageUserId?: string | null) {
  const userId = information?.user_id ?? pageUserId;
  if (userId) {
    return `https://pic.paziresh24.com/api/image/${userId}`;
  }
  const image = typeof information?.image === 'string' ? information.image.trim() : '';
  if (image && !isDefaultDoctorProfileImage(image) && !/noimage/i.test(image)) {
    if (/^https?:\/\//i.test(image)) return image;
    return `https://www.paziresh24.com${image.startsWith('/') ? '' : '/'}${image}`;
  }
  return defaultDoctorProfileImageUrl(information?.gender);
}

/** Smaller pic CDN variant for above-the-fold avatar (LCP). */
export function resolveDoctorProfileLcpImageUrl(
  information: DoctorImageSource | undefined,
  pageUserId?: string | null,
) {
  const userId = information?.user_id ?? pageUserId;
  if (userId) {
    return picUserImageUrl(userId);
  }
  return resolveDoctorProfileImageUrl(information, pageUserId);
}

/** Drop `?p=thumb` so lightbox loads the full-resolution pic CDN image. */
export function toDoctorProfileLightboxSrc(src: string): string {
  if (!src || !isPicUserImageUrl(src)) return src;
  try {
    const url = new URL(src);
    if (url.searchParams.get('p') === 'thumb') {
      url.searchParams.delete('p');
    }
    const out = url.toString();
    return out.endsWith('?') ? out.slice(0, -1) : out;
  } catch {
    return src.replace(/\?p=thumb$/, '').replace(/([?&])p=thumb(&|$)/, (_, sep, end) => (end === '&' ? sep : ''));
  }
}

export function canPreviewDoctorProfileImage(src?: string | null): boolean {
  if (!src?.trim()) return false;
  if (isDefaultDoctorProfileImage(src)) return false;
  if (/noimage/i.test(src)) return false;
  return true;
}

export { isPicUserImageUrl, NO_DOCTOR_PROFILE_IMAGE, NO_DOCTOR_PROFILE_IMAGE_MEN, NO_DOCTOR_PROFILE_IMAGE_WOMEN };
